import { useCallback, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import prettyBytes from 'pretty-bytes';
import { Paperclip, UploadCloud, X } from 'lucide-react';
import { DEFAULT_ATTACHMENT_MAX_BYTES } from '@raptor/shared';

import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { ApiError } from '@/lib/api/client';
import { useAuth } from '@/lib/auth/useAuth';
import { cn } from '@/lib/utils';

import { uploadAttachment } from '../api';
import type { AttachmentUploadResponse } from '../types';

type SlotStatus = 'uploading' | 'done' | 'failed';

interface Slot {
  key: string;
  file: File;
  status: SlotStatus;
  loaded: number;
  total: number;
  error?: string;
  // Populated once the server returns 201.
  serverId?: string;
  serverFilename?: string;
  serverSizeBytes?: number;
  abort?: AbortController;
}

interface Props {
  onChange: (attachmentIds: string[]) => void;
  onBusyChange: (busy: boolean) => void;
  disabled?: boolean;
  maxBytes?: number;
}

// Multi-file attachment picker. Each file uploads immediately and independently
// via XHR through the API (POST /issues/attachments). Progress is per-file. The
// picker owns the list of in-flight and completed slots; the parent form only
// sees the ids of successful uploads via onChange, and whether anything is
// still in flight via onBusyChange.
//
// The client-side size check is intentionally soft — the server is authoritative.
// A soft check gives instant feedback for the common case of a plainly-oversize
// file, but if the server later returns 413/400 we surface *its* message so the
// limit stays a single source of truth.
export function AttachmentPicker({
  onChange,
  onBusyChange,
  disabled,
  maxBytes = DEFAULT_ATTACHMENT_MAX_BYTES,
}: Props) {
  const { accessToken } = useAuth();
  const [slots, setSlots] = useState<Slot[]>([]);
  // Stable per-slot key generator; File identity alone isn't reliable if the
  // user picks the same file twice.
  const nextKey = useRef(0);

  const emitState = useCallback(
    (next: Slot[]) => {
      onChange(next.filter((s) => s.status === 'done' && s.serverId).map((s) => s.serverId!));
      onBusyChange(next.some((s) => s.status === 'uploading'));
    },
    [onChange, onBusyChange],
  );

  const updateSlot = useCallback(
    (key: string, patch: Partial<Slot>) => {
      setSlots((prev) => {
        const next = prev.map((s) => (s.key === key ? { ...s, ...patch } : s));
        emitState(next);
        return next;
      });
    },
    [emitState],
  );

  const startUpload = useCallback(
    async (slot: Slot) => {
      const controller = new AbortController();
      slot.abort = controller;

      try {
        const result: AttachmentUploadResponse = await uploadAttachment({
          file: slot.file,
          token: accessToken,
          signal: controller.signal,
          onProgress: (loaded, total) => {
            updateSlot(slot.key, { loaded, total });
          },
        });
        updateSlot(slot.key, {
          status: 'done',
          serverId: result.id,
          serverFilename: result.filename,
          serverSizeBytes: result.sizeBytes,
          loaded: slot.file.size,
          total: slot.file.size,
        });
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          // Silent — user removed the file mid-upload; slot is already gone.
          return;
        }
        const message =
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Upload failed';
        updateSlot(slot.key, { status: 'failed', error: message });
      }
    },
    [accessToken, updateSlot],
  );

  const onDrop = useCallback(
    (accepted: File[]) => {
      if (accepted.length === 0) return;

      // Split into ones that pass the soft cap and ones that are pre-rejected
      // client-side so we can render a per-file error without ever hitting the
      // server.
      const now = Date.now();
      const additions: Slot[] = accepted.map((file, i) => {
        const key = `slot-${now}-${nextKey.current++}-${i}`;
        if (file.size > maxBytes) {
          return {
            key,
            file,
            status: 'failed',
            loaded: 0,
            total: file.size,
            error: `File exceeds ${prettyBytes(maxBytes)} limit`,
          };
        }
        return {
          key,
          file,
          status: 'uploading',
          loaded: 0,
          total: file.size,
        };
      });

      setSlots((prev) => {
        const next = [...prev, ...additions];
        emitState(next);
        return next;
      });

      // Kick off the network for the ones that passed the soft cap.
      for (const slot of additions) {
        if (slot.status === 'uploading') {
          void startUpload(slot);
        }
      }
    },
    [emitState, maxBytes, startUpload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    disabled,
  });

  const removeSlot = (key: string) => {
    setSlots((prev) => {
      const target = prev.find((s) => s.key === key);
      if (target?.abort && target.status === 'uploading') {
        target.abort.abort();
      }
      const next = prev.filter((s) => s.key !== key);
      emitState(next);
      return next;
    });
  };

  const retrySlot = (key: string) => {
    setSlots((prev) => {
      const target = prev.find((s) => s.key === key);
      if (!target) return prev;
      const restarted: Slot = {
        ...target,
        status: 'uploading',
        loaded: 0,
        total: target.file.size,
        error: undefined,
      };
      const next = prev.map((s) => (s.key === key ? restarted : s));
      emitState(next);
      void startUpload(restarted);
      return next;
    });
  };

  return (
    <div className="space-y-3" data-testid="issue-attachments">
      <div
        {...getRootProps()}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed p-6 text-center transition-colors',
          isDragActive ? 'border-primary bg-primary/5' : 'border-slate-300',
          disabled && 'pointer-events-none opacity-60',
        )}
        data-testid="issue-attachments-dropzone"
      >
        <input {...getInputProps()} data-testid="issue-attachments-input" />
        <UploadCloud className="mb-2 h-6 w-6 text-slate-400" aria-hidden="true" />
        <div className="text-sm text-slate-700">
          {isDragActive
            ? 'Drop files here'
            : 'Drag files here or click to attach (optional, max ' +
              prettyBytes(maxBytes) +
              ' each)'}
        </div>
      </div>

      {slots.length > 0 && (
        <ul className="space-y-2" data-testid="issue-attachments-list">
          {slots.map((slot) => (
            <li
              key={slot.key}
              className="flex items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3"
              data-testid={`issue-attachment-${slot.key}`}
              data-status={slot.status}
            >
              <Paperclip className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium">{slot.file.name}</span>
                  <span className="whitespace-nowrap text-xs text-muted-foreground">
                    {prettyBytes(slot.file.size)}
                  </span>
                </div>

                {slot.status === 'uploading' && (
                  <>
                    <Progress value={progressPercent(slot)} />
                    <div className="text-xs text-muted-foreground">
                      Uploading… {progressPercent(slot)}%
                    </div>
                  </>
                )}
                {slot.status === 'done' && <div className="text-xs text-emerald-700">Attached</div>}
                {slot.status === 'failed' && slot.error && (
                  <div className="flex items-center gap-2 text-xs text-destructive">
                    <span>{slot.error}</span>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto p-0 text-xs"
                      onClick={() => retrySlot(slot.key)}
                    >
                      Retry
                    </Button>
                  </div>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeSlot(slot.key)}
                aria-label={`Remove ${slot.file.name}`}
                className="h-6 w-6 shrink-0"
              >
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function progressPercent(slot: { loaded: number; total: number }): number {
  if (!slot.total) return 0;
  return Math.min(100, Math.round((slot.loaded / slot.total) * 100));
}
